// Helpers WebAuthn para el desbloqueo biométrico del Vault (Touch ID / Face ID /
// Windows Hello). Hecho a mano (~60 líneas) en vez de sumar @simplewebauthn/browser:
// lo único que hace falta es serializar ArrayBuffers ↔ base64url alrededor de
// navigator.credentials.create/get.

function b64urlToBuffer(b64url) {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob(b64 + pad);
  const buf = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) buf[i] = raw.charCodeAt(i);
  return buf.buffer;
}

function bufferToB64url(buf) {
  const bytes = new Uint8Array(buf);
  let str = '';
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function webauthnSupported() {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials;
}

// ¿Este equipo tiene autenticador de plataforma (Touch ID / Face ID / Hello)?
export async function platformAuthenticatorAvailable() {
  if (!webauthnSupported()) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

// options = lo que devuelve el backend (generateRegistrationOptions). Devuelve el
// objeto serializado que espera verifyRegistrationResponse.
export async function startRegistration(options) {
  const publicKey = {
    ...options,
    challenge: b64urlToBuffer(options.challenge),
    user: { ...options.user, id: b64urlToBuffer(options.user.id) },
    excludeCredentials: (options.excludeCredentials || []).map(c => ({ ...c, id: b64urlToBuffer(c.id) })),
  };
  const cred = await navigator.credentials.create({ publicKey });
  return {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: cred.type,
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    response: {
      attestationObject: bufferToB64url(cred.response.attestationObject),
      clientDataJSON: bufferToB64url(cred.response.clientDataJSON),
      transports: cred.response.getTransports ? cred.response.getTransports() : [],
    },
  };
}

// options = lo que devuelve generateAuthenticationOptions. Devuelve el objeto que
// espera verifyAuthenticationResponse.
export async function startAuthentication(options) {
  const publicKey = {
    ...options,
    challenge: b64urlToBuffer(options.challenge),
    allowCredentials: (options.allowCredentials || []).map(c => ({ ...c, id: b64urlToBuffer(c.id) })),
  };
  const cred = await navigator.credentials.get({ publicKey });
  return {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: cred.type,
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    response: {
      authenticatorData: bufferToB64url(cred.response.authenticatorData),
      clientDataJSON: bufferToB64url(cred.response.clientDataJSON),
      signature: bufferToB64url(cred.response.signature),
      userHandle: cred.response.userHandle ? bufferToB64url(cred.response.userHandle) : null,
    },
  };
}

// Nombre amigable del dispositivo para la lista de enrolados.
export function guessDeviceName() {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone (Face ID / Touch ID)';
  if (/iPad/.test(ua)) return 'iPad (Face ID / Touch ID)';
  if (/Macintosh/.test(ua)) return 'Mac (Touch ID)';
  if (/Windows/.test(ua)) return 'Windows (Hello)';
  if (/Android/.test(ua)) return 'Android';
  return 'Dispositivo';
}
